/**
 * Deploy-skew detection for Server Actions (Sentry RANKSURF-8G).
 *
 * A tab loaded on an old deployment posts a Server Action id the new deployment no longer has.
 * Next answers with `Failed to find Server Action …` on the server, and the browser throws
 * `UnrecognizedActionError` ("Server Action "<id>" was not found on the server"). Reloading the
 * page fixes it, so the user is shown a reload prompt (`PageError`) and the event is not an alert.
 *
 * Matched by name and message rather than `unstable_isUnrecognizedActionError` so it works on the
 * server, in tests, and on an error that lost its prototype crossing a boundary.
 */
export const SERVER_ACTION_SKEW_PATTERN =
  /Failed to find Server Action|Server Action "[^"]*" was not found on the server/

export function isServerActionSkewError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return error.name === 'UnrecognizedActionError' || SERVER_ACTION_SKEW_PATTERN.test(error.message)
}

/** sessionStorage key holding the epoch-ms of the last skew "Reload" press in this tab. */
const SKEW_RELOAD_KEY = 'deploy-skew-reload-at'
/** Skew that recurs within this window of the user pressing Reload is not skew. */
export const SKEW_RELOAD_WINDOW_MS = 120_000

/**
 * Records that the user just pressed Reload for skew; `false` when storage is unavailable
 * (private mode throws) — the recurrence guard then degrades to "always skew", never to a crash.
 */
export function markSkewReload(now: number = Date.now()): boolean {
  try {
    window.sessionStorage.setItem(SKEW_RELOAD_KEY, String(now))
    return true
  } catch {
    return false
  }
}

/** True when a skew error appears shortly after this tab reloaded for skew. */
export function skewRecurredAfterReload(now: number = Date.now()): boolean {
  try {
    const at = Number(window.sessionStorage.getItem(SKEW_RELOAD_KEY))
    return Number.isFinite(at) && at > 0 && now - at >= 0 && now - at < SKEW_RELOAD_WINDOW_MS
  } catch {
    return false
  }
}

/** `skew` → reload prompt; `recurred` → a real bug (ordinary card + exception); `ordinary` → not skew. */
export type BoundaryErrorKind = 'skew' | 'recurred' | 'ordinary'

export function classifyBoundaryError(error: unknown, now: number = Date.now()): BoundaryErrorKind {
  if (!isServerActionSkewError(error)) return 'ordinary'
  return skewRecurredAfterReload(now) ? 'recurred' : 'skew'
}
